/**
 * Set Quantity Handler - Works with both [setofX] and setofX tag formats
 * Automatically adjusts quantities in cart based on product tags
 * Plus/minus buttons increment/decrement by set amount
 * Bypasses Shopify's max quantity validation
 */

class SetQuantityHandler {
  constructor() {
    this.init();
  }

  init() {
    // Wait for cart data to be available
    if (typeof window.cartItemsData === 'undefined') {
      console.log('Cart items data not yet loaded, retrying...');
      setTimeout(() => this.init(), 100);
      return;
    }

    console.log('Set Quantity Handler initialized');
    console.log('Cart items data:', window.cartItemsData);

    // Override max quantity validation
    this.overrideMaxValidation();
    
    // Handle quantity changes
    this.setupQuantityListeners();
    
    // Handle initial cart load
    this.adjustInitialQuantities();
  }

  /**
   * Override Shopify's max quantity validation
   */
  overrideMaxValidation() {
    // Remove max attributes that might interfere
    document.querySelectorAll('.quantity__input').forEach(input => {
      // Store original max if it exists
      const originalMax = input.getAttribute('max');
      if (originalMax && originalMax !== '9999') {
        input.setAttribute('data-original-max', originalMax);
      }
      // Set high max
      input.setAttribute('max', '9999');
    });

    // Intercept form submission to bypass max validation
    const cartForm = document.querySelector('form#cart');
    if (cartForm) {
      cartForm.addEventListener('submit', (e) => {
        // Remove max attributes before submission
        document.querySelectorAll('.quantity__input').forEach(input => {
          input.removeAttribute('max');
        });
      });
    }
  }

  /**
   * Parse set quantity from product tags (handles both formats)
   */
  parseSetQuantity(tags) {
    if (!tags || !Array.isArray(tags)) return 1;

    for (const tag of tags) {
      const tagLower = tag.toLowerCase();
      
      if (tagLower.includes('setof')) {
        // Remove brackets if present and extract number
        const cleanTag = tagLower.replace(/[\[\]]/g, '');
        const match = cleanTag.match(/setof(\d+)/);
        
        if (match && match[1]) {
          const qty = parseInt(match[1], 10);
          if (qty > 1) {
            console.log(`Found set quantity: ${qty} from tag: ${tag}`);
            return qty;
          }
        }
      }
    }
    
    return 1; // Default to 1 if no set tag found
  }

  /**
   * Adjust quantities on initial cart load
   */
  adjustInitialQuantities() {
    Object.entries(window.cartItemsData).forEach(([variantId, itemData]) => {
      const setQuantity = itemData.setQuantity || this.parseSetQuantity(itemData.tags);
      
      if (setQuantity > 1 && itemData.hasSetTag) {
        console.log(`Product ${itemData.handle} has set quantity of ${setQuantity}`);
        
        // Find the quantity input for this variant
        const quantityInput = document.querySelector(`input[data-quantity-variant-id="${variantId}"]`);
        
        if (quantityInput) {
          const currentQty = parseInt(quantityInput.value, 10);
          
          // If not a multiple of set quantity, adjust it
          if (currentQty % setQuantity !== 0) {
            const adjustedQty = Math.ceil(currentQty / setQuantity) * setQuantity;
            console.log(`Adjusting quantity from ${currentQty} to ${adjustedQty}`);
            this.updateQuantity(quantityInput, adjustedQty);
          }
        }
      }
    });
  }

  /**
   * Setup listeners for quantity changes
   */
  setupQuantityListeners() {
    // Listen to all quantity inputs
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('quantity__input')) {
        this.handleQuantityChange(e.target);
      }
    });

    // Listen to plus/minus buttons - INTERCEPT AND HANDLE SET QUANTITIES
    document.addEventListener('click', (e) => {
      const button = e.target.closest('.quantity__button');
      if (!button) return;

      const quantityInput = button.closest('quantity-input').querySelector('.quantity__input');
      const variantId = quantityInput.dataset.quantityVariantId;
      const itemData = window.cartItemsData[variantId];
      
      if (!itemData) return;

      const setQuantity = itemData.setQuantity || this.parseSetQuantity(itemData.tags);
      
      // If it's a set quantity product, handle it specially
      if (setQuantity > 1 && itemData.hasSetTag) {
        e.preventDefault(); // Stop the default behavior
        e.stopPropagation();
        
        const currentQty = parseInt(quantityInput.value, 10) || 0;
        const isPlus = button.name === 'plus';
        
        let newQty;
        if (isPlus) {
          // Increment by set quantity
          newQty = currentQty + setQuantity;
        } else {
          // Decrement by set quantity
          newQty = Math.max(0, currentQty - setQuantity);
        }
        
        console.log(`${isPlus ? 'Increasing' : 'Decreasing'} by ${setQuantity}: ${currentQty} → ${newQty}`);
        
        // Update the quantity
        quantityInput.value = newQty;
        
        // Trigger change event to update cart
        const changeEvent = new Event('change', { bubbles: true });
        quantityInput.dispatchEvent(changeEvent);
      }
      // For non-set products, let the default behavior happen
    });
  }

  /**
   * Handle quantity change event (for manual input)
   */
  handleQuantityChange(input) {
    const variantId = input.dataset.quantityVariantId;
    const itemData = window.cartItemsData[variantId];
    
    if (!itemData) {
      console.log('No item data found for variant:', variantId);
      return;
    }

    const setQuantity = itemData.setQuantity || this.parseSetQuantity(itemData.tags);
    
    // Only enforce set quantities if product has the tag
    if (setQuantity <= 1 || !itemData.hasSetTag) {
      return;
    }

    const currentQty = parseInt(input.value, 10);
    
    if (currentQty % setQuantity !== 0) {
      // Round to nearest multiple
      let adjustedQty = Math.round(currentQty / setQuantity) * setQuantity;
      
      // Ensure at least one set if they entered a positive number
      if (adjustedQty < setQuantity && currentQty > 0) {
        adjustedQty = setQuantity;
      }
      
      console.log(`Adjusting manually entered quantity from ${currentQty} to ${adjustedQty} (set of ${setQuantity})`);
      input.value = adjustedQty;
      
      // Show notification to user
      this.showSetQuantityMessage(input, setQuantity, adjustedQty);
    }
  }

  /**
   * Update quantity value and trigger cart update
   */
  updateQuantity(input, newQuantity) {
    input.value = newQuantity;
    
    // Trigger change event to update cart
    const event = new Event('change', { bubbles: true });
    input.dispatchEvent(event);
  }

  /**
   * Show message to user about set quantity adjustment
   */
  showSetQuantityMessage(input, setQuantity, adjustedQty) {
    const cartItem = input.closest('.cart-item');
    const errorDiv = cartItem.querySelector('.cart-item__error');
    
    if (errorDiv) {
      const errorText = errorDiv.querySelector('.cart-item__error-text');
      
      if (errorText) {
        errorText.textContent = `This item is sold in sets of ${setQuantity}. Quantity adjusted to ${adjustedQty}.`;
        errorDiv.style.display = 'flex';
        
        // Hide message after 3 seconds
        setTimeout(() => {
          errorDiv.style.display = 'none';
          errorText.textContent = '';
        }, 3000);
      }
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new SetQuantityHandler();
  });
} else {
  new SetQuantityHandler();
}